'use client'

import { CircledUserIcon } from '@repo/ui/CircledUserIcon'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { ClientSelect } from '@repo/ui/ClientSelect'
import { ConflictScoreIcon } from '@repo/ui/ConflictScoreIcon'
import { Divider } from '@mui/material'
import { LargeClientAddIcon } from '@repo/ui/LargeClientAddIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useCallback, useEffect, useState } from 'react'
import { useClientStore } from '@repo/store/useClientStore'
import { useFeeEarnerStore } from '@repo/store/useFeeEarnerStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useNotificationStore } from '@repo/store/useNotificationStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIButton from '@mui/material/Button'
import MUIRadio from '@mui/material/Radio'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const NewMatterNotificationApprovalClientMapping = ({
  wideMode,
  handlePrevNext,
  testMode = false,
  updateStepState
}) => {
  const { newNotification, setIsNewClient } = useNotificationStore()
  const {
    updateState: updateNewClientState,
    existingClient,
    updateWizardClientState,
    resetWizardClientState
  } = useNewClientStore()
  const { updateWizardMatterState, resetWizardMatterState } = useNewMatterStore()
  const {
    branch,
    department,
    fetchBranchById,
    fetchDepartmentById,
    fetchRemunerationTypeById,
    fetchRemunerationTypes,
    fetchWorkflowTypeById,
    remunerationType,
    workflowType,
    resetBranch,
    resetDepartment,
    resetRemunerationType,
    resetWorkflowType
  } = useLookupsStore()
  const {
    fetchFeeEarnerById,
    fetchSupervisingFeeEarnerById,
    feeEarner,
    supervisingFeeEarner,
    resetFeeEarner,
    resetSupervisingFeeEarner
  } = useFeeEarnerStore()
  const { fetchSuggestedClients, suggestedClients } = useClientStore()

  const [loading, setLoading] = useState(true)
  const [disableContinue, setDisableContinue] = useState(true)
  const [selectedClient, setSelectedClient] = useState('')
  const [suggestedClientsTab, setSuggestedClientsTab] = useState(true)
  const [searchClientsTab, setSearchClientsTab] = useState(false)
  const [createClientsTab, setCreateClientsTab] = useState(false)

  const updateWizardStores = useCallback(async () => {
    if (testMode) return

    resetFeeEarner()

    newNotification.matter.feeEarnerId &&
      (await fetchFeeEarnerById({ feeEarnerId: newNotification.matter.feeEarnerId }))

    resetSupervisingFeeEarner()

    newNotification.matter.supervisingFeeEarnerId &&
      (await fetchSupervisingFeeEarnerById({ supervisingFeeEarnerId: newNotification.matter.supervisingFeeEarnerId }))

    resetBranch()

    newNotification.matter.branchId && (await fetchBranchById({ branchId: newNotification.matter.branchId }))

    resetDepartment()

    newNotification.matter.departmentId &&
      (await fetchDepartmentById({ departmentId: newNotification.matter.departmentId }))

    resetWorkflowType()

    newNotification.matter.workflowTypeId &&
      (await fetchWorkflowTypeById({ workflowTypeId: newNotification.matter.workflowTypeId }))

    resetRemunerationType()

    newNotification.matter.crmSchemeId &&
      newNotification.matter.privateOrLegalAid &&
      (await fetchRemunerationTypes({
        crmSchemeId: newNotification.matter.crmSchemeId,
        privateOrLegalAid: newNotification.matter.privateOrLegalAid
      }))

    newNotification.matter.remunerationTypeId &&
      (await fetchRemunerationTypeById({ remunerationTypeId: newNotification.matter.remunerationTypeId }))
  }, [
    fetchBranchById,
    fetchDepartmentById,
    fetchFeeEarnerById,
    fetchRemunerationTypeById,
    fetchRemunerationTypes,
    fetchSupervisingFeeEarnerById,
    fetchWorkflowTypeById,
    newNotification?.matter,
    testMode,
    resetBranch,
    resetDepartment,
    resetFeeEarner,
    resetRemunerationType,
    resetSupervisingFeeEarner,
    resetWorkflowType
  ])

  useEffect(() => {
    if (testMode) return
    const getSuggestedClients = async () => {
      setLoading(true)
      await resetWizardClientState()
      await resetWizardMatterState()

      await fetchSuggestedClients(newNotification.clients[0].namedClients)

      setLoading(false)
    }

    getSuggestedClients()
  }, [fetchSuggestedClients, resetWizardClientState, resetWizardMatterState, newNotification?.clients, testMode])

  useEffect(() => {
    if (testMode) return

    const updateClientMatterWizardStates = async () => {
      const clienLevelCustomDataPageFieldValues = newNotification?.customDataPageFields?.filter(field => field.isClientLevel === true) || []
      const matterLevelCustomDataPageFieldValues = newNotification?.customDataPageFields?.filter(field => field.isClientLevel !== true) || []

      selectedClient && (await updateWizardClientState({
        clientData: selectedClient,
        customDataPageFieldValues: clienLevelCustomDataPageFieldValues
      }))

      await updateWizardMatterState({
        matterData: newNotification.matter,
        branch: branch,
        department: department,
        feeEarner: feeEarner,
        supervisingFeeEarner: supervisingFeeEarner,
        remunerationType: remunerationType,
        workflowType: workflowType,
        customDataPageFieldValues: matterLevelCustomDataPageFieldValues
      })
    }

    updateClientMatterWizardStates()
  }, [
    branch,
    department,
    existingClient,
    feeEarner,
    newNotification?.matter,
    remunerationType,
    selectedClient,
    supervisingFeeEarner,
    updateNewClientState,
    updateWizardClientState,
    updateWizardMatterState,
    updateWizardStores,
    workflowType,
    testMode,
    resetWizardClientState,
    resetWizardMatterState,
    newNotification?.customDataPageFields
  ])

  useEffect(() => {
    if (testMode) return

    if (searchClientsTab) {
      setSelectedClient(existingClient)
      updateWizardStores()
    } else {
      updateWizardStores()
    }
  }, [existingClient, searchClientsTab, updateWizardStores, testMode])

  useEffect(() => {
    if (testMode) return

    if (!createClientsTab) {
      if (selectedClient === '' || selectedClient === undefined || selectedClient === null) {
        setDisableContinue(true)
      }
    }
  }, [createClientsTab, selectedClient, testMode])

  const handleChange = async (client) => {
    setSelectedClient(client)
    setDisableContinue(false)
    await updateWizardStores()
  }

  const handleClickContinue = () => {
    searchClientsTab === true || suggestedClientsTab === true ? setIsNewClient(false) : setIsNewClient(true)
    updateStepState({ status: 'completed', statusText: 'Complete' })
    handlePrevNext(1)
  }

  const handleBackButtonClick = () => {
    handlePrevNext(-1)
  }

  const calculateConflictScore = (client) => {
    let conflictScore = 0
    if (client.rank > 5) conflictScore = 4
    else if (client.rank > 3) conflictScore = 3
    else if (client.rank > 1) conflictScore = 2
    else if (client.rank > 0) conflictScore = 1
    return conflictScore
  }

  return (
    <>
      <WizardPanel data-testid="NewMatterNotificationApprovalClientMapping">
        <WizardFormSection>
          <WizardIconTitle
            title="Client mapping"
            icon={CircledUserIcon}
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              mt: '0.25rem',
              ml: '0.5rem',
              mr: '0.5rem',
              [`& .MuiStack-root`]: {
                alignItems: 'center'
              }
            }}
          />
          <MUITypography variant="body1" sx={{ ml: '0.5rem', mr: '0.5rem' }}>
            {suggestedClientsTab
              ? `Based on the data received, we think the following clients might be suitable matches.`
              : searchClientsTab
                ? `Search for a different client`
                : createClientsTab
                  ? `Create a new client`
                  : ''}
          </MUITypography>

          <MUIStack gap={4} direction="column" alignItems="flex-start" sx={{ width: '100%' }}>
            <MUIStack
              gap={4}
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ width: '100%', pr: '2rem' }}
            >
              <MUIButton
                variant="text"
                color="secondary"
                size="small"
                onClick={() => {
                  setSuggestedClientsTab(false)
                  setSearchClientsTab(true)
                  setCreateClientsTab(false)
                  setDisableContinue(true)
                  setSelectedClient('')
                  resetWizardClientState()
                }}
                sx={{
                  display: !searchClientsTab && !createClientsTab ? 'block' : 'none',
                  padding: 0,
                  textTransform: 'capitalize',
                  textDecoration: 'underline',
                  fontSize: '0.8rem',
                  textUnderlineOffset: '4px'
                }}
              >
                Search for a different client
              </MUIButton>
              <MUIButton
                variant="text"
                color="secondary"
                size="small"
                onClick={() => {
                  setSuggestedClientsTab(true)
                  setSearchClientsTab(false)
                  setCreateClientsTab(false)
                  setDisableContinue(true)
                }}
                sx={{
                  display: !suggestedClientsTab ? 'block' : 'none',
                  padding: 0,
                  textTransform: 'capitalize',
                  textDecoration: 'underline',
                  fontSize: '0.8rem',
                  textUnderlineOffset: '4px'
                }}
              >
                See suggested Clients
              </MUIButton>
              <Divider orientation="vertical" flexItem />
              <MUIButton
                variant="text"
                color="secondary"
                size="small"
                onClick={() => {
                  setSuggestedClientsTab(false)
                  setSearchClientsTab(false)
                  setCreateClientsTab(true)
                  setSelectedClient('')
                  resetWizardClientState()
                  updateWizardClientState({
                    clientData: newNotification?.clients[0],
                    customDataPageFieldValues: newNotification?.customDataPageFields?.filter(field => field.isClientLevel === true) || []
                  })
                  setDisableContinue(false)
                }}
                sx={{
                  display: suggestedClientsTab || searchClientsTab ? 'block' : 'none',
                  padding: 0,
                  textTransform: 'capitalize',
                  textDecoration: 'underline',
                  fontSize: '0.8rem',
                  textUnderlineOffset: '4px'
                }}
              >
                Create a new client
              </MUIButton>
              <MUIButton
                variant="text"
                color="secondary"
                size="small"
                onClick={() => {
                  setSuggestedClientsTab(false)
                  setSearchClientsTab(true)
                  setCreateClientsTab(false)
                  setDisableContinue(true)
                }}
                sx={{
                  display: createClientsTab && !searchClientsTab ? 'block' : 'none',
                  padding: 0,
                  textTransform: 'capitalize',
                  textDecoration: 'underline',
                  fontSize: '0.8rem',
                  textUnderlineOffset: '4px'
                }}
              >
                Search for a different client
              </MUIButton>
            </MUIStack>

            {suggestedClientsTab && suggestedClients ? (
              loading ? (
                <MUIStack
                  gap={4}
                  direction="column"
                  alignItems="center"
                  justifyContent={'center'}
                  sx={{ width: 'calc(100% - 4rem)', margin: '0 2rem', height: '200px' }}
                >
                  <LoadingState />
                </MUIStack>
              ) : suggestedClients.length > 0 ? (
                suggestedClients.slice(0, 20).map((client) => (
                  <MUIStack
                    gap={4}
                    direction="column"
                    alignItems="flex-start"
                    sx={{ width: 'calc(100% - 4rem)', margin: '0 2rem' }}
                    key={client.clientNo}
                  >
                    <MUIStack
                      backgroundColor="#F5F6F8"
                      gap={4}
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="center"
                      sx={{ width: '100%', padding: '1rem 1.5rem', borderRadius: '4px', cursor: 'pointer' }}
                      onClick={() => handleChange(client)}
                    >
                      <MUIRadio
                        checked={selectedClient === client}
                        onChange={() => handleChange(client)}
                        value={client}
                        name="radio-buttons"
                        inputProps={{ 'aria-label': client.clientNo }}
                      />
                      <MUIStack
                        gap={1}
                        direction="column"
                        alignItems="flex-start"
                        sx={{ width: '100%', cursor: 'pointer' }}
                      >
                        <MUIStack gap={1} direction="row" justifyContent="flex-start" alignItems="center">
                          <ClientMatterDetailsPill clientNo={client.clientNo} sx={{ top: '3px' }} />
                          <MUITypography variant="body1" sx={{ cursor: 'pointer', mb: 0 }}>
                            {client.title ?? ''} {client.forename} {client.surname}
                          </MUITypography>
                        </MUIStack>

                        {client.emailAddress ? (
                          <MUITypography variant="body2" sx={{ cursor: 'pointer' }}>
                            {client.emailAddress}
                          </MUITypography>
                        ) : null}
                      </MUIStack>

                      <ConflictScoreIcon
                        sx={{ width: '56px', height: '14px', pointerEvents: 'none' }}
                        score={calculateConflictScore(client)}
                      />
                    </MUIStack>
                  </MUIStack>
                ))
              ) : (
                <MUIStack
                  gap={4}
                  direction="column"
                  alignItems="center"
                  justifyContent={'center'}
                  sx={{ width: 'calc(100% - 4rem)', margin: '0 2rem', height: '200px' }}
                >
                  <MUITypography variant="body1" sx={{ mt: 4, ml: 2 }}>
                    No suggested clients found
                  </MUITypography>
                </MUIStack>
              )
            ) : null}
            {searchClientsTab && (
              <MUIStack gap={4} direction="column" alignItems="flex-start" sx={{ width: 'calc(100% - 4rem)' }}>
                <ClientSelect
                  size="medium"
                  sx={{ width: '400px', marginTop: '20px' }}
                  name="Client"
                  searchable
                  label="Search client"
                  value={existingClient}
                  onChange={(value) => {
                    updateNewClientState({ key: 'existingClient', value })
                    setSelectedClient(value)
                    setDisableContinue(false)
                  }}
                />
              </MUIStack>
            )}
            {createClientsTab && (
              <MUIStack
                gap={4}
                direction="column"
                alignItems="center"
                sx={{ width: 'calc(100% - 4rem)', margin: '0 auto' }}
              >
                <LargeClientAddIcon sx={{ height: '90px', width: '90px', mt: '80px', mb: '32px' }} />
                <MUITypography variant="h6" sx={{ mb: '80px' }}>
                  Please click continue to create a new client
                </MUITypography>
              </MUIStack>
            )}
          </MUIStack>
        </WizardFormSection>
      </WizardPanel>
      <WizardFooter
        wideMode={wideMode}
        continueButtonText="Continue"
        backButtonVisible={true}
        continueButtonDisabled={disableContinue}
        handleClickBack={handleBackButtonClick}
        handleClickContinue={handleClickContinue}
      />
    </>
  )
}

NewMatterNotificationApprovalClientMapping.propTypes = {
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  testMode: PropTypes.bool,
  updateStepState: PropTypes.func
}
